import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { I18nProvider, useI18n } from "./i18n";
import { Layout } from "./components/Layout";
import { SUPER_ADMIN } from "./components/navigation";
import { LoginPage } from "./features/auth/LoginPage";
import type { Perm } from "./auth/AuthContext";
import { Card, PageLoading } from "./components/ui";

// Lazy-loaded so each route ships its own chunk instead of one bundle that
// every page (including the login screen) has to download and parse first.
const DashboardPage = lazy(() =>
  import("./features/dashboard/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  })),
);
const ReviewsPage = lazy(() =>
  import("./features/reviews/ReviewsPage").then((m) => ({
    default: m.ReviewsPage,
  })),
);
const NewReviewPage = lazy(() =>
  import("./features/reviews/NewReviewPage").then((m) => ({
    default: m.NewReviewPage,
  })),
);
const ReviewDetailPage = lazy(() =>
  import("./features/reviews/ReviewDetailPage").then((m) => ({
    default: m.ReviewDetailPage,
  })),
);
const PoliciesPage = lazy(() =>
  import("./features/policies/PoliciesPage").then((m) => ({
    default: m.PoliciesPage,
  })),
);
const PolicyDocumentPage = lazy(() =>
  import("./features/policies/PolicyDocumentPage").then((m) => ({
    default: m.PolicyDocumentPage,
  })),
);
const RulesPage = lazy(() =>
  import("./features/rules/RulesPage").then((m) => ({ default: m.RulesPage })),
);
const RuleDetailPage = lazy(() =>
  import("./features/rules/RuleDetailPage").then((m) => ({
    default: m.RuleDetailPage,
  })),
);
const EducateSagePage = lazy(() =>
  import("./features/educateSage/EducateSagePage").then((m) => ({
    default: m.EducateSagePage,
  })),
);
const EducateSageWizard = lazy(() =>
  import("./features/educateSage/EducateSageWizard").then((m) => ({
    default: m.EducateSageWizard,
  })),
);
const LessonDetailPage = lazy(() =>
  import("./features/educateSage/LessonDetailPage").then((m) => ({
    default: m.LessonDetailPage,
  })),
);
const SageWorkflowPage = lazy(() =>
  import("./features/settings/sageWorkflow/SageWorkflowPage").then((m) => ({
    default: m.SageWorkflowPage,
  })),
);
const DictionaryPage = lazy(() =>
  import("./features/dictionary/DictionaryPage").then((m) => ({
    default: m.DictionaryPage,
  })),
);
const MembersPage = lazy(() =>
  import("./features/members/MembersPage").then((m) => ({
    default: m.MembersPage,
  })),
);
const AuditPage = lazy(() =>
  import("./features/audit/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const QueueHealthPage = lazy(() =>
  import("./features/queueHealth/QueueHealthPage").then((m) => ({
    default: m.QueueHealthPage,
  })),
);
const AskSagePage = lazy(() =>
  import("./features/askSage/AskSagePage").then((m) => ({ default: m.AskSagePage })),
);
const OrganizationAskSageLogPage = lazy(() =>
  import("./features/askSage/AskSageLogPage").then((m) => ({
    default: m.OrganizationAskSageLogPage,
  })),
);
const PlatformAskSageLogPage = lazy(() =>
  import("./features/askSage/AskSageLogPage").then((m) => ({
    default: m.PlatformAskSageLogPage,
  })),
);
const ConfigPage = lazy(() =>
  import("./features/config/ConfigPage").then((m) => ({
    default: m.ConfigPage,
  })),
);
const AiQualityPage = lazy(() =>
  import("./features/aiQuality/AiQualityPage").then((m) => ({
    default: m.AiQualityPage,
  })),
);
const EngineQualityPage = lazy(() =>
  import("./features/aiQuality/EngineQualityPage").then((m) => ({
    default: m.EngineQualityPage,
  })),
);
const OrganizationsPage = lazy(() =>
  import("./features/organizations/OrganizationsPage").then((m) => ({
    default: m.OrganizationsPage,
  })),
);
const VaultsageOrphansPage = lazy(() =>
  import("./features/organizations/VaultsageOrphansPage").then((m) => ({
    default: m.VaultsageOrphansPage,
  })),
);
const ProfilePage = lazy(() =>
  import("./features/profile/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const SuperAdminPage = lazy(() =>
  import("./features/super-admin/SuperAdminPage").then((m) => ({
    default: m.SuperAdminPage,
  })),
);
const LlmSettingsPage = lazy(() =>
  import("./features/super-admin/LlmSettingsPage").then((m) => ({
    default: m.LlmSettingsPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import("./features/admin-users/AdminUsersPage").then((m) => ({
    default: m.AdminUsersPage,
  })),
);
const SupportPage = lazy(() =>
  import("./features/support/SupportPage").then((m) => ({ default: m.SupportPage })),
);
const SupportTicketPage = lazy(() =>
  import("./features/support/SupportTicketPage").then((m) => ({
    default: m.SupportTicketPage,
  })),
);
const SupportInboxPage = lazy(() =>
  import("./features/support/SupportInboxPage").then((m) => ({
    default: m.SupportInboxPage,
  })),
);
const SupportInboxTicketPage = lazy(() =>
  import("./features/support/SupportInboxTicketPage").then((m) => ({
    default: m.SupportInboxTicketPage,
  })),
);
const AdminUserDetailPage = lazy(() =>
  import("./features/admin-users/AdminUserDetailPage").then((m) => ({
    default: m.AdminUserDetailPage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequirePermission({
  permission,
  children,
}: {
  permission: Perm;
  children: React.ReactNode;
}) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <Navigate to="/" replace />;
}

function RequirePlatformSuperAdmin({
  children,
}: {
  children: React.ReactNode;
}) {
  const { platformRole } = useAuth();
  return platformRole === "SUPER_ADMIN" ? (
    <>{children}</>
  ) : (
    <Navigate to="/" replace />
  );
}

function RequireTenantPermission({
  permission,
  children,
}: {
  permission: Perm;
  children: React.ReactNode;
}) {
  const { activeOrganization, platformRole } = useAuth();
  const { t } = useI18n();

  if (!activeOrganization) {
    if (platformRole === "SUPER_ADMIN") {
      return <Navigate to={SUPER_ADMIN.home} replace />;
    }
    return (
      <Card>
        <h1 className="text-lg font-semibold">{t("organizationRequired")}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {t("organizationRequiredHelp")}
        </p>
      </Card>
    );
  }

  return (
    <RequirePermission permission={permission}>{children}</RequirePermission>
  );
}

function TenantRoute({
  permission,
  children,
}: {
  permission: Perm;
  children: React.ReactNode;
}) {
  return (
    <RequireTenantPermission permission={permission}>
      {children}
    </RequireTenantPermission>
  );
}

function SuperAdminOutlet() {
  return (
    <RequirePlatformSuperAdmin>
      <Outlet />
    </RequirePlatformSuperAdmin>
  );
}

/** Forwards a bookmarked top-level admin URL into the /super-admin subtree. */
function LegacySuperAdminRedirect({ to }: { to: (params: Record<string, string | undefined>) => string }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

export function App() {
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  element={
                    <Protected>
                      <Layout />
                    </Protected>
                  }
                >
                  <Route
                    path="/"
                    element={
                      <TenantRoute permission="REVIEW_CREATE">
                        <DashboardPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/reviews"
                    element={
                      <TenantRoute permission="REVIEW_CREATE">
                        <ReviewsPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/reviews/new"
                    element={
                      <TenantRoute permission="REVIEW_CREATE">
                        <NewReviewPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/reviews/:id"
                    element={
                      <TenantRoute permission="REVIEW_CREATE">
                        <ReviewDetailPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/queue"
                    element={
                      <TenantRoute permission="REVIEW_VIEW_ALL">
                        <Navigate to="/reviews?view=queue" replace />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/policies"
                    element={
                      <TenantRoute permission="POLICY_VIEW">
                        <PoliciesPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/policies/:policyId/versions/:versionId"
                    element={
                      <TenantRoute permission="POLICY_VIEW">
                        <PolicyDocumentPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/rules"
                    element={
                      <TenantRoute permission="RULE_VIEW">
                        <RulesPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/compliance-scope"
                    element={<Navigate to="/sage-workflow?view=map" replace />}
                  />
                  <Route
                    path="/sage-workflow"
                    element={
                      <TenantRoute permission="RULE_VIEW">
                        <SageWorkflowPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/guide-matrix"
                    element={<Navigate to="/sage-workflow?view=table" replace />}
                  />
                  <Route
                    path="/rules/:id"
                    element={
                      <TenantRoute permission="RULE_VIEW">
                        <RuleDetailPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/channels"
                    element={<Navigate to="/sage-workflow" replace />}
                  />
                  <Route
                    path="/educate-sage"
                    element={
                      <TenantRoute permission="RULE_VIEW">
                        <EducateSagePage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/educate-sage/new"
                    element={
                      <TenantRoute permission="RULE_MANAGE">
                        <EducateSageWizard />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/educate-sage/:id"
                    element={
                      <TenantRoute permission="RULE_VIEW">
                        <LessonDetailPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/dictionary"
                    element={
                      <TenantRoute permission="RULE_VIEW">
                        <DictionaryPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/members"
                    element={
                      <TenantRoute permission="MEMBER_VIEW">
                        <MembersPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/audit"
                    element={
                      <TenantRoute permission="AUDIT_VIEW">
                        <AuditPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/queue-health"
                    element={
                      <TenantRoute permission="AUDIT_VIEW">
                        <QueueHealthPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/config"
                    element={
                      <TenantRoute permission="CONFIG_MANAGE">
                        <ConfigPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/ai-quality"
                    element={
                      <TenantRoute permission="AI_QUALITY_VIEW">
                        <AiQualityPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/ask-sage"
                    element={
                      <TenantRoute permission="ASK_SAGE_USE">
                        <AskSagePage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/ask-sage-log"
                    element={
                      <TenantRoute permission="ASK_SAGE_VIEW_ORG">
                        <OrganizationAskSageLogPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/support"
                    element={
                      <TenantRoute permission="SUPPORT_CREATE">
                        <SupportPage />
                      </TenantRoute>
                    }
                  />
                  <Route
                    path="/support/:id"
                    element={
                      <TenantRoute permission="SUPPORT_CREATE">
                        <SupportTicketPage />
                      </TenantRoute>
                    }
                  />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path={SUPER_ADMIN.home} element={<SuperAdminOutlet />}>
                    <Route index element={<SuperAdminPage />} />
                    <Route path="organizations" element={<OrganizationsPage />} />
                    <Route path="vaultsage-orphans" element={<VaultsageOrphansPage />} />
                    <Route path="admin-users" element={<AdminUsersPage />} />
                    <Route path="admin-users/:userId" element={<AdminUserDetailPage />} />
                    <Route path="support-inbox" element={<SupportInboxPage />} />
                    <Route path="support-inbox/:id" element={<SupportInboxTicketPage />} />
                    <Route path="ask-sage-log" element={<PlatformAskSageLogPage />} />
                    <Route path="engine-quality" element={<EngineQualityPage />} />
                    <Route path="llm-settings" element={<LlmSettingsPage />} />
                  </Route>
                  <Route
                    path="/organizations"
                    element={<Navigate to={SUPER_ADMIN.organizations} replace />}
                  />
                  <Route
                    path="/vaultsage-orphans"
                    element={<Navigate to={SUPER_ADMIN.vaultsageOrphans} replace />}
                  />
                  <Route
                    path="/admin-users"
                    element={<Navigate to={SUPER_ADMIN.adminUsers} replace />}
                  />
                  <Route
                    path="/admin-users/:userId"
                    element={
                      <LegacySuperAdminRedirect
                        to={({ userId }) => SUPER_ADMIN.adminUser(userId ?? "")}
                      />
                    }
                  />
                  <Route
                    path="/support-inbox"
                    element={<Navigate to={SUPER_ADMIN.supportInbox} replace />}
                  />
                  <Route
                    path="/support-inbox/:id"
                    element={
                      <LegacySuperAdminRedirect
                        to={({ id }) => SUPER_ADMIN.supportInboxTicket(id ?? "")}
                      />
                    }
                  />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
